import React, { useState, useMemo, useRef, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, GeoJSON } from "react-leaflet";
import debounce from 'lodash.debounce'; // <-- Add this import at top
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import BuildingPreview from "./BuildingsPreview/BuildingPreview";
import BuildingDetails from "./BuildingsPreview/BuildingDetails";
import NeighborhoodLayer from "./NeighborhoodLayer";
import NeighborhoodSidebar from './NeighborhoodSidebar';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const fullDetailMarkerIcon = L.icon({
  iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const summaryMarkerIcon = L.icon({
  iconUrl: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});


const UNIT_DISPLAY_ZOOM = 16;
const DEBOUNCE_DELAY = 500;
const TOLERANCE = 0.0001; // adjust as necessary

const MapView = ({
  setBoundingBox,
  highlightBuilding,
  handleBuildingClick,
  detailedBuilding,
  setDetailedBuilding,
  setOpenedBuildingID,
  setCurrentZoom,
  currentZoom,
  setSelectedNeighborhood,
}) => {
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [buildingSummaries, setBuildingSummaries] = useState([]);

  const prevBoundsRef = useRef(null);
  const prevZoomRef = useRef(null);

const fetchNeighborhoods = async (bounds) => {
  const { south, north, west, east } = bounds;
  try {
    const res = await fetch(
      `${API_BASE_URL}/properties/neighborhoods-in-view?south=${south}&north=${north}&west=${west}&east=${east}`
    );
    if (!res.ok) throw new Error("Neighborhoods API failed.");
    const geojson = await res.json();
	console.log(geojson);
    // 🚨 CRITICAL FIX HERE:
    setNeighborhoods(geojson);  

  } catch (error) {
    console.error("Error fetching neighborhoods:", error);
    setNeighborhoods(null);
  }
};





  const fetchBuildings = async (bounds) => {
    const params = new URLSearchParams({
      south: bounds.south,
      north: bounds.north,
      west: bounds.west,
      east: bounds.east,
    });

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/properties/building-summaries?${params}`);
      if (!response.ok) throw new Error("Buildings API call failed.");
      const data = await response.json();
      setBuildingSummaries(data);
    } catch (error) {
      console.error("Error fetching buildings:", error);
    }
  };

	const debouncedUpdate = useRef(
	  debounce((bounds, zoom) => {
		  console.log("debounce");
		  console.log(bounds);
		setBoundingBox(bounds);
		fetchNeighborhoods(bounds);
		if (zoom >= UNIT_DISPLAY_ZOOM) {
		  fetchBuildings(bounds);
		} else {
		  setBuildingSummaries([]);
		}
	  }, DEBOUNCE_DELAY)
	).current;

  const boundsMovedEnough = (oldB, newB, threshold = TOLERANCE) => {
	  return (
		Math.abs(oldB.south - newB.south) > threshold ||
		Math.abs(oldB.north - newB.north) > threshold ||
		Math.abs(oldB.west - newB.west) > threshold ||
		Math.abs(oldB.east - newB.east) > threshold
	  );
	};

const MapEvents = () => {
  const map = useMapEvents({
    moveend: () => {
	  console.log('Move');
      const bounds = map.getBounds();
      const zoom = map.getZoom();

      const currentBounds = {
        south: bounds.getSouth(),
        north: bounds.getNorth(),
        west: bounds.getWest(),
        east: bounds.getEast(),
      };

      const boundsChanged = boundsMovedEnough(prevBoundsRef.current, currentBounds);
      const zoomChanged = prevZoomRef.current !== zoom;

      if (boundsChanged || zoomChanged) {
        prevBoundsRef.current = currentBounds;
        prevZoomRef.current = zoom;

        debouncedUpdate(currentBounds, zoom);
        setCurrentZoom(zoom);
      }
    },
  });

 useEffect(() => {
    if (!map) return;
	if (prevZoomRef.current) return;

    const initialBounds = map.getBounds();
    const initialZoom = map.getZoom();

    const currentBounds = {
      south: initialBounds.getSouth(),
      north: initialBounds.getNorth(),
      west: initialBounds.getWest(),
      east: initialBounds.getEast(),
    };

    prevBoundsRef.current = currentBounds;
    prevZoomRef.current = initialZoom;

    debouncedUpdate(currentBounds, initialZoom);
    setCurrentZoom(initialZoom);
  }, [map, debouncedUpdate]);

  return null;
};


	
  const handleMarkerClick = (building) => {
    setSelectedBuilding(building);
    handleBuildingClick(building.BuildingID);
  };

  const handleViewDetails = (building) => {
    setShowDetail(true);
    setSelectedBuilding(null);
    setOpenedBuildingID(building.BuildingID);
  };

  const memoizedMarkers = useMemo(() => {
    return (buildingSummaries ?? [])
      .filter((building) => {
        const lat = Number(building.Latitude);
        const lng = Number(building.Longitude);
        return !isNaN(lat) && !isNaN(lng);
      })
      .map((building) => (
        <Marker
          key={building.BuildingID}
          position={[Number(building.Latitude), Number(building.Longitude)]}
          icon={building.Priority === "high" ? fullDetailMarkerIcon : summaryMarkerIcon}
          eventHandlers={{
            mouseover: () => highlightBuilding(building.BuildingID),
            mouseout: () => highlightBuilding(null),
            click: () => handleMarkerClick(building),
          }}
        >
          <Popup>
            <div className="popup-content">
              {building.ImageURL && <img src={building.ImageURL} alt={building.Address} className="popup-image" />}
              <h3 className="popup-title">{building.Address}</h3>
              <p className="popup-location">{building.City}, {building.State}</p>
              <p className="popup-price">${building.MinRent} - ${building.MaxRent} / month</p>
              <p className="popup-units">{building.AvailableOffers ?? 0} Available Units</p>
              <button className="popup-btn" onClick={() => handleViewDetails(building)}>
                See Details
              </button>
            </div>
          </Popup>
        </Marker>
      ));
  }, [buildingSummaries, highlightBuilding]);

  return (
    <div style={{ position: "relative", height: "100vh" }}>
      <NeighborhoodSidebar activeFilter={activeFilter} setActiveFilter={setActiveFilter} />
      <MapContainer center={[42.3601, -71.0589]} zoom={13} zoomControl={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <MapEvents />
		{currentZoom < UNIT_DISPLAY_ZOOM && neighborhoods && (
		  <NeighborhoodLayer
			key={`${activeFilter}-${JSON.stringify(neighborhoods)}`} // Add this line
			neighborhoods={neighborhoods}
			activeFilter={activeFilter}
			setSelectedNeighborhood={setSelectedNeighborhood}
		  />
		)}
        {currentZoom >= UNIT_DISPLAY_ZOOM && memoizedMarkers}
      </MapContainer>

      {selectedBuilding && (
        <BuildingPreview
          building={selectedBuilding}
          onClose={() => setSelectedBuilding(null)}
          onViewDetails={handleViewDetails}
        />
      )}

      {detailedBuilding && showDetail && (
        <BuildingDetails building={detailedBuilding} onClose={() => setShowDetail(null)} />
      )}
    </div>
  );
};

export default MapView;
